import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { BottomPopupService } from 'src/app/services/bottom-popup.service';
import { ReclamationService } from 'src/app/services/reclamation.service';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-reclamation-finish-image-upload',
	templateUrl: './reclamation-finish-image-upload.component.html',
	styleUrls: ['./reclamation-finish-image-upload.component.scss'],
})
export class ReclamationFinishImageUploadComponent implements OnDestroy, OnInit {
	private _bottomPopup = inject(BottomPopupService);
	private sanitizer = inject(DomSanitizer);
	private _reclamation = inject(ReclamationService);
	constructor() {}

	@Input() index: number;

	@Output() readonly finished = new EventEmitter<File>();

	@Output() readonly startedUploading = new EventEmitter<void>();

	image$ = new BehaviorSubject<{ file: File | null; preview: SafeUrl }>({ file: null, preview: '' });

	status$ = new BehaviorSubject<'idle' | 'uploading' | 'success' | 'error'>('idle');

	destroy$ = new Subject<void>();

	ngOnInit(): void {
		this.status$.pipe(takeUntil(this.destroy$)).subscribe(status => {
			if (status === 'success') {
				this.finished.emit(this.image$.value.file);
			}
		});
	}

	ngOnDestroy(): void {
		if (this.image$.value.preview) {
			URL.revokeObjectURL(this.image$.value.preview as string);
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	onFileSelected(event: Event) {
		if (!(event.target instanceof HTMLInputElement)) {
			throw new Error('event.target is not an HTMLInputElement');
		}
		const input = event.target;
		if (!input.files?.length) {
			return;
		}
		let file = input.files[0];
		const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
		if (!allowedTypes.includes(file.type)) {
			this.status$.next('error');
			this._bottomPopup.show('סוג תמונה אינו נתמך');
			return;
		}

		file = new File([file], `${this._reclamation.selectedTask$.value.RekID}-${this.index + 1}.jpg`, { type: file.type });
		this.handleReadingAndUpload(file);
	}

	handleReadingAndUpload(file: File) {
		const reader = new FileReader();

		reader.onload = (e: ProgressEvent<FileReader>) => {
			const preview: string = typeof e.target.result === 'string' ? e.target.result : URL.createObjectURL(file);

			if (environment.development) {
				console.log('skipping upload in development');
				const safePreview: SafeUrl = this.sanitizer.sanitize(SecurityContext.URL, preview);
				this.image$.next({ file, preview: safePreview });
				this.status$.next('success');
				this._bottomPopup.show('תמונה עלתה בהצלחה!');

				return;
			}

			this.status$.next('uploading');
			this.uploadImageToServer(file)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: res => {
						const safePreview: SafeUrl = this.sanitizer.sanitize(SecurityContext.URL, preview);
						this.image$.next({ file, preview: safePreview });
						this.status$.next('success');
						this._bottomPopup.show('תמונה עלתה בהצלחה!');
					},
					error: err => {
						this.markAsError();
						console.error(err);
					},
				});
		};
		reader.readAsDataURL(file);
		this.status$.next('idle');
	}

	markAsError() {
		this.status$.next(this.image$.value.file ? 'idle' : 'error');

		this._bottomPopup.show('העלאת תמונה נכשלה');
	}

	uploadImageToServer(file: File) {
		if (!file) {
			throw new Error('no file');
		}
		return this._reclamation.uploadImageToServer(file);
	}
}
